/**
 * @file theSupperClub.js
 * Entrance choreography for the Last Supper Club page, built on native browser
 * APIs only: IntersectionObserver to fire each move as a section scrolls into
 * view and the Web Animations API to run the moves and the hero intro. There is
 * no animation library and no build step.
 *
 * Rather than one reveal for everything, each section gets its own move tuned to
 * its layout: the badge pops, the pillars cascade and their icons pop in after
 * the card, the events and gallery images wipe open while their text slides in,
 * the chef and venue blocks slide from opposite sides to mirror the layout, and
 * the bookings lines stagger up. The plans below are pure: an element goes in, a
 * list of moves comes out. Applying and running those moves is the only part
 * that touches the page.
 *
 * Moves animate the independent "translate" and "scale" CSS properties (and
 * "clip-path"), never "transform", so the transforms the stylesheet already sets
 * (the badge across the hero seam, the gallery zig-zag offsets) keep working.
 * When the visitor prefers reduced motion, or the browser lacks an API we need,
 * nothing animates and every block is shown at once.
 */

(function () {
	'use strict';

	/**
	 * One move in a section's plan: which element animates, the from and to styles,
	 * and the timing. Stored as data so the section plans read as a list of moves.
	 * @typedef {object} Move
	 * @property {Element} target - The element to animate.
	 * @property {object} from - Starting styles (also used to hide the element up front).
	 * @property {object} to - Resting styles.
	 * @property {number} duration - Length of the move in milliseconds.
	 * @property {number} delay - Wait before the move starts, in milliseconds.
	 * @property {string} easing - A CSS easing function.
	 */

	// Easing curves. "overshoot" passes its target and eases back, for a confident pop.
	const EASE = {
		smooth: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
		overshoot: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
		wipe: 'cubic-bezier(0.45, 0, 0.15, 1)',
	};

	// Move lengths, in milliseconds.
	const DURATION = {
		line: 540,
		base: 680,
		slow: 820,
		wipe: 880,
	};

	// Gaps between staggered moves, in milliseconds.
	const STAGGER = {
		pillars: 120,
		gallery: 90,
		lines: 80,
	};

	// How the observer decides a block has arrived. The negative bottom margin lets
	// a block start moving a little before it is fully on screen. A page opened more
	// than openGuard pixels down is treated as resumed: whatever is already on
	// screen or above it appears without replaying, so scrolling back up reveals
	// nothing and animations are reserved for new blocks arriving from below.
	const REVEAL = {
		rootMargin: '0px 0px -12% 0px',
		threshold: 0.15,
		openGuard: 4,
	};

	// Reveals play on the way down only. These track the live scroll offset and
	// direction so a block met while scrolling up, or one already passed on a
	// resumed page, can skip its animation and simply appear.
	let lastScrollOffset = 0;
	let scrollingDown = true;

	// The hero intro. The four strips fade in on a tight cascade, the mosaic holds
	// for a beat, then the hero zooms up while the other strips dissolve outward
	// from it, so the hero looks like it grows into the frame and absorbs them
	// rather than sliding them away. The hero sits full behind the strips, so the
	// dissolve never exposes a gap.
	const ENTRANCE = {
		duration: 1200,
		stagger: 100,
		scaleFrom: '1.04',
		easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
	};

	// The takeover: after a deliberate hold, the hero scales up and the strips fade
	// out, the nearest to the hero first and the far one last so the clearing ripples
	// outward. The scale and the fades hold their end state so the hero stays full.
	const BLOOM = {
		hold: 2400,
		zoomDuration: 3000,
		scaleTo: '1.1',
		fadeDuration: 3000,
		fadeStagger: 100,
		easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
	};

	// The hero sits in the second slot from the left; the strips fade out radiating
	// from this column.
	const HERO = {
		column: 1,
	};

	// How long to wait for the hero images to decode before starting the intro
	// regardless, so a slow network cannot leave the hero hidden behind its
	// placeholder. Sits comfortably under the inline script's 4s safety net.
	const LOAD = {
		heroTimeout: 2000,
	};

	// Once the intro has played, the hero settles into a slideshow: the other
	// photographs cross-fade through one another at full size on an endless loop.
	// A long dwell keeps it calm behind the page rather than busy and distracting.
	const LOOP = {
		startDelay: 1500,
		interval: 5000,
		fadeDuration: 1500,
		easing: 'cubic-bezier(0.45, 0, 0.55, 1)',
	};

	init();

	/**
	 * Entry point. Runs the enhancement once the document has been parsed.
	 * @returns {void}
	 */
	function init() {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', enhance, { once: true });
			return;
		}
		enhance();
	}

	/**
	 * Decides whether to animate, then either shows everything at once or starts
	 * the reveal choreography and the parallax.
	 * @returns {void}
	 */
	function enhance() {
		if (!shouldAnimate()) {
			// Tell the stylesheet to guarantee every [data-reveal] block is shown,
			// then do nothing else.
			document.documentElement.classList.add('tsc-no-anim');
			return;
		}

		setupReveals();
		setupHeroIntro();
	}

	/**
	 * Builds a plan for every revealable block, hides each block, then runs its
	 * plan the first time it scrolls into view.
	 * @returns {void}
	 */
	function setupReveals() {
		const blocks = Array.from(document.querySelectorAll('[data-reveal]'));
		const plans = new Map();

		blocks.forEach((block) => {
			plans.set(block, planFor(block));
			hide(plans.get(block));
		});

		trackScroll();
		// Wait a frame so a reloaded page has settled on its restored scroll
		// position before we decide which blocks count as already seen.
		requestAnimationFrame(() => {
			armReveals(blocks, plans);
		});
	}

	/**
	 * Sorts the blocks into those that animate as they arrive from below and those
	 * that simply appear. On a page opened part-way down, anything already on screen
	 * or above it appears with no animation, so scrolling back up plays nothing; the
	 * rest animate, but only while the reader is moving down the page.
	 * @param {Element[]} blocks - Every reveal block.
	 * @param {Map<Element, Move[]>} plans - Each block's move plan.
	 * @returns {void}
	 */
	function armReveals(blocks, plans) {
		const resumed = scrollOffset() > REVEAL.openGuard;
		scrollingDown = !resumed;

		const pending = [];
		blocks.forEach((block) => {
			const top = block.getBoundingClientRect().top;
			const below = top >= window.innerHeight;
			const above = top < 0;
			if (above || (resumed && !below)) {
				showInstant(plans.get(block));
			} else {
				pending.push(block);
			}
		});

		observeOnce(pending, (block) => {
			if (scrollingDown) {
				run(plans.get(block));
			} else {
				showInstant(plans.get(block));
			}
		}, REVEAL);
	}

	/**
	 * Keeps the live scroll direction up to date so reveals can tell whether the
	 * reader is moving down the page or back up it.
	 * @returns {void}
	 */
	function trackScroll() {
		lastScrollOffset = scrollOffset();
		window.addEventListener('scroll', () => {
			const offset = scrollOffset();
			if (offset === lastScrollOffset) {
				return;
			}
			scrollingDown = offset > lastScrollOffset;
			lastScrollOffset = offset;
		}, { passive: true });
	}

	/**
	 * The document's vertical scroll offset, with a fallback for older engines.
	 * @returns {number} - Pixels scrolled from the top of the page.
	 */
	function scrollOffset() {
		if (typeof window.scrollY === 'number') {
			return window.scrollY;
		}
		return document.documentElement.scrollTop;
	}

	/**
	 * Drops a block straight into its resting state with no animation, for blocks
	 * that should appear rather than play: content above a resumed page, or a block
	 * met while scrolling back up.
	 * @param {Move[]} moves - The block's moves.
	 * @returns {void}
	 */
	function showInstant(moves) {
		moves.forEach((step) => {
			settle(step.target, step.to);
		});
	}

	/**
	 * Chooses the move plan for one block based on which section it belongs to.
	 * @param {Element} block - A [data-reveal] element.
	 * @returns {Move[]} - The moves to run when the block arrives.
	 */
	function planFor(block) {
		if (block.matches('.tsc-badge__medallion')) {
			return planBadge(block);
		}
		if (block.matches('.tsc-intro__inner')) {
			return planIntro(block);
		}
		if (block.matches('.tsc-pillar')) {
			return planPillar(block);
		}
		if (block.matches('.tsc-events__card')) {
			return planEventsCard(block);
		}
		if (block.matches('.tsc-gallery__item')) {
			return planGalleryItem(block);
		}
		if (block.matches('.tsc-feature__media')) {
			return planFeatureMedia(block);
		}
		if (block.matches('.tsc-feature__body')) {
			return planFeatureBody(block);
		}
		if (block.matches('.tsc-stay__panel')) {
			return planStayPanel(block);
		}
		if (block.matches('.tsc-bookings__card')) {
			return planBookingsCard(block);
		}
		return planRise(block);
	}

	// The hero badge pops in with a slight overshoot. Scale is independent of the
	// stylesheet's translate(-50%, 70%), so the medallion keeps its position.
	function planBadge(block) {
		return [
			move(block, { opacity: '0', scale: '0.82' }, { opacity: '1', scale: '1' }, DURATION.slow, 0, EASE.overshoot),
		];
	}

	// The intro statement and its button rise in, one just after the other.
	function planIntro(block) {
		const children = Array.from(block.children);
		return children.map((child, index) => {
			return move(child, { opacity: '0', translate: '0 22px' }, { opacity: '1', translate: '0 0' }, DURATION.base, index * STAGGER.lines, EASE.smooth);
		});
	}

	// Each pillar card lifts and settles, staggered across the three; the icon then
	// pops in just behind its own card for a small beat of life.
	function planPillar(block) {
		const base = indexInParent(block) * STAGGER.pillars;
		const icon = block.querySelector('.tsc-pillar__icon');

		const moves = [
			move(block, { opacity: '0', translate: '0 28px', scale: '0.97' }, { opacity: '1', translate: '0 0', scale: '1' }, DURATION.slow, base, EASE.smooth),
		];
		if (icon) {
			moves.push(move(icon, { opacity: '0', scale: '0.6' }, { opacity: '1', scale: '1' }, DURATION.base, base + 160, EASE.overshoot));
		}
		return moves;
	}

	// The event image (when there is one) wipes open left to right while its text
	// lines slide in from the right, one after another.
	function planEventsCard(block) {
		const image = block.querySelector('.tsc-events__img');
		const lines = Array.from(block.querySelector('.tsc-events__body').children);

		// The menu variant of the card has no photo, so only its text lines move.
		const moves = [];
		if (image) {
			moves.push(move(image, { opacity: '0', clipPath: 'inset(0 100% 0 0)', scale: '1.08' }, { opacity: '1', clipPath: 'inset(0 0 0 0)', scale: '1' }, DURATION.wipe, 0, EASE.wipe));
		}
		lines.forEach((line, index) => {
			moves.push(move(line, { opacity: '0', translate: '34px 0' }, { opacity: '1', translate: '0 0' }, DURATION.line, 140 + index * STAGGER.lines, EASE.smooth));
		});
		return moves;
	}

	// Each gallery photograph wipes upward into place, staggered along the row. The
	// figure keeps its zig-zag transform; only the image inside wipes and settles.
	function planGalleryItem(block) {
		const image = block.querySelector('.tsc-gallery__img');
		const base = indexInParent(block) * STAGGER.gallery;
		return [
			move(image, { opacity: '0', clipPath: 'inset(100% 0 0 0)', scale: '1.08' }, { opacity: '1', clipPath: 'inset(0 0 0 0)', scale: '1' }, DURATION.wipe, base, EASE.wipe),
		];
	}

	// The chef and venue photographs wipe open from the side their layout sits on,
	// so the reveal direction mirrors the page.
	function planFeatureMedia(block) {
		const image = block.querySelector('.tsc-feature__img');

		let fromClip = 'inset(0 100% 0 0)';
		if (block.closest('.tsc-feature--venue')) {
			fromClip = 'inset(0 0 0 100%)';
		}
		return [
			move(image, { opacity: '0', clipPath: fromClip, scale: '1.08' }, { opacity: '1', clipPath: 'inset(0 0 0 0)', scale: '1' }, DURATION.wipe, 0, EASE.wipe),
		];
	}

	// The chef text slides in from the right; the venue text mirrors it from the
	// left. The lines within each block cascade.
	function planFeatureBody(block) {
		let fromTranslate = '40px 0';
		if (block.closest('.tsc-feature--venue')) {
			fromTranslate = '-40px 0';
		}

		const lines = Array.from(block.children);
		return lines.map((line, index) => {
			return move(line, { opacity: '0', translate: fromTranslate }, { opacity: '1', translate: '0 0' }, DURATION.base, index * STAGGER.lines, EASE.smooth);
		});
	}

	// The stay photograph wipes up inside the green panel while the text lines
	// beside it stagger in.
	function planStayPanel(block) {
		const image = block.querySelector('.tsc-stay__img');
		const lines = Array.from(block.querySelector('.tsc-stay__body').children);

		const moves = [];
		if (image) {
			moves.push(move(image, { opacity: '0', clipPath: 'inset(100% 0 0 0)', scale: '1.08' }, { opacity: '1', clipPath: 'inset(0 0 0 0)', scale: '1' }, DURATION.wipe, 0, EASE.wipe));
		}
		lines.forEach((line, index) => {
			moves.push(move(line, { opacity: '0', translate: '0 22px' }, { opacity: '1', translate: '0 0' }, DURATION.line, 160 + index * STAGGER.lines, EASE.smooth));
		});
		return moves;
	}

	// The white bookings card fades up off the dark photo, then its lines stagger
	// in from below.
	function planBookingsCard(block) {
		const lines = Array.from(block.children);

		const moves = [
			move(block, { opacity: '0', translate: '0 24px' }, { opacity: '1', translate: '0 0' }, DURATION.slow, 0, EASE.smooth),
		];
		lines.forEach((line, index) => {
			moves.push(move(line, { opacity: '0', translate: '0 18px' }, { opacity: '1', translate: '0 0' }, DURATION.line, 200 + index * STAGGER.lines, EASE.smooth));
		});
		return moves;
	}

	// The fallback for anything without a section of its own (the sample-menu button).
	function planRise(block) {
		return [
			move(block, { opacity: '0', translate: '0 24px' }, { opacity: '1', translate: '0 0' }, DURATION.base, 0, EASE.smooth),
		];
	}

	/**
	 * Builds one move. A small factory so the section plans above read as a list of
	 * moves rather than a wall of repeated option objects.
	 * @param {Element} target - The element to animate.
	 * @param {object} from - Starting styles.
	 * @param {object} to - Resting styles.
	 * @param {number} duration - Length in milliseconds.
	 * @param {number} delay - Wait before starting, in milliseconds.
	 * @param {string} easing - A CSS easing function.
	 * @returns {Move}
	 */
	function move(target, from, to, duration, delay, easing) {
		return {
			target: target,
			from: from,
			to: to,
			duration: duration,
			delay: delay,
			easing: easing,
		};
	}

	/**
	 * Puts every move's target into its starting state up front, so the blocks are
	 * hidden before they scroll into view and have somewhere to animate from.
	 * @param {Move[]} moves - The moves for one block.
	 * @returns {void}
	 */
	function hide(moves) {
		moves.forEach((step) => {
			applyStyles(step.target, step.from);
		});
	}

	/**
	 * Runs every move for one block, then writes the resting state onto each target
	 * and drops the animation so nothing lingers.
	 * @param {Move[]} moves - The moves for one block.
	 * @returns {void}
	 */
	function run(moves) {
		moves.forEach((step) => {
			const animation = step.target.animate([step.from, step.to], {
				duration: step.duration,
				delay: step.delay,
				easing: step.easing,
				fill: 'forwards',
			});

			animation.addEventListener('finish', () => {
				settle(step.target, step.to);
				animation.cancel();
			}, { once: true });
		});
	}

	/**
	 * Writes a block's resting state to inline styles. Opacity is pinned on; the
	 * transient properties are cleared so the element rests naturally and the
	 * stylesheet keeps control of any base transform it sets.
	 * @param {Element} target - The animated element.
	 * @param {object} to - The move's resting styles.
	 * @returns {void}
	 */
	function settle(target, to) {
		Object.keys(to).forEach((property) => {
			if (property === 'opacity') {
				target.style.opacity = '1';
				return;
			}
			target.style[property] = '';
		});
	}

	/**
	 * Applies a set of inline styles to an element.
	 * @param {Element} target - The element to style.
	 * @param {object} styles - Property names (camelCase) to values.
	 * @returns {void}
	 */
	function applyStyles(target, styles) {
		Object.keys(styles).forEach((property) => {
			target.style[property] = styles[property];
		});
	}

	/**
	 * Plays the hero intro: the strips fade in on a cascade, the mosaic holds, then
	 * the hero zooms up while the strips dissolve outward from it. Needs the strip
	 * and reveal elements to be present.
	 * @returns {void}
	 */
	function setupHeroIntro() {
		const reveal = document.querySelector('.tsc-hero__reveal');
		const strips = Array.from(document.querySelectorAll('.tsc-hero__strip'));
		if (!reveal || strips.length === 0) {
			return;
		}

		// Hold the intro until the hero images have decoded, so the cascade fades in
		// ready images rather than playing over blank slots that pop in once loaded.
		// The placeholder colour shows in the meantime. A timeout keeps a slow image
		// from stalling the intro indefinitely.
		const columns = [reveal, ...strips];
		whenImagesReady(columns).then(() => {
			revealColumns(columns);
			growHero(reveal, strips).then(() => {
				loopHero(strips);
			});
		});
	}

	/**
	 * Fades each column in with a soft scale settle, staggered left to right by its
	 * slot. The stagger is tight so the four read as one cascade, not four steps.
	 * @param {Element[]} columns - The reveal and strip elements, each with a slot.
	 * @returns {void}
	 */
	function revealColumns(columns) {
		columns.forEach((column) => {
			const slot = Number(column.dataset.tscCol);
			column.animate(
				[
					{ opacity: 0, scale: ENTRANCE.scaleFrom },
					{ opacity: 1, scale: '1' },
				],
				{
					duration: ENTRANCE.duration,
					delay: slot * ENTRANCE.stagger,
					easing: ENTRANCE.easing,
					fill: 'both',
				}
			);
		});
	}

	/**
	 * After the hold, zooms the hero up to fill the frame and dissolves the other
	 * strips outward from it. The hero sits full behind them, so as each strip fades
	 * the hero is already there and no gap shows. Both hold their end state so the
	 * hero stays full once the intro is done.
	 * @param {Element} reveal - The hero image, sitting full behind the strips.
	 * @param {Element[]} strips - The three other strips.
	 * @returns {Promise<void>} - Resolves once the grow has fully played out.
	 */
	function growHero(reveal, strips) {
		const zoom = reveal.animate(
			[
				{ scale: '1' },
				{ scale: BLOOM.scaleTo },
			],
			{
				duration: BLOOM.zoomDuration,
				delay: BLOOM.hold,
				easing: BLOOM.easing,
				fill: 'forwards',
			}
		);

		const fades = strips.map((strip) => {
			const distance = Math.abs(Number(strip.dataset.tscCol) - HERO.column);
			return strip.animate(
				[
					{ opacity: 1 },
					{ opacity: 0 },
				],
				{
					duration: BLOOM.fadeDuration,
					delay: BLOOM.hold + (distance - 1) * BLOOM.fadeStagger,
					easing: BLOOM.easing,
					fill: 'forwards',
				}
			);
		});

		return whenAllFinished([zoom].concat(fades));
	}

	/**
	 * After the intro, settles the hero into an endless cross-fade slideshow of the
	 * other photographs at full size. Each was a quarter-width strip; here they fill
	 * the frame like the hero behind them and fade through one another in turn. The
	 * incoming photograph is stacked on top and faded in over the one it replaces,
	 * so each change reads as a clean dissolve, and only one fade per photograph is
	 * ever live so animations cannot pile up over a long-running loop.
	 * @param {Element[]} strips - The photographs to cycle through.
	 * @returns {void}
	 */
	function loopHero(strips) {
		strips.forEach((strip) => {
			strip.style.left = '0';
			strip.style.width = '100%';
		});

		const count = strips.length;
		const playing = new Array(count).fill(null);
		let index = -1;

		function advance() {
			index = (index + 1) % count;
			stackForCrossfade(strips, index);
			const incoming = strips[index];
			const fade = incoming.animate(
				[
					{ opacity: 0 },
					{ opacity: 1 },
				],
				{
					duration: LOOP.fadeDuration,
					easing: LOOP.easing,
					fill: 'both',
				}
			);
			if (playing[index]) {
				playing[index].cancel();
			}
			playing[index] = fade;
			setTimeout(advance, LOOP.interval);
		}

		setTimeout(advance, LOOP.startDelay);
	}

	/**
	 * Stacks the slideshow photographs so the incoming one sits on top and the one
	 * it is replacing sits just beneath it, for a clean cross-fade. Ordered by how
	 * recently each was shown, and kept well below the badge above the hero.
	 * @param {Element[]} strips - The slideshow photographs.
	 * @param {number} incomingIndex - The photograph fading in.
	 * @returns {void}
	 */
	function stackForCrossfade(strips, incomingIndex) {
		const count = strips.length;
		strips.forEach((strip, slot) => {
			const back = (incomingIndex - slot + count) % count;
			strip.style.zIndex = String(count - back);
		});
	}

	/**
	 * Resolves once every given animation has finished, ignoring any that are
	 * cancelled, so callers can chain work after a group of moves completes.
	 * @param {Animation[]} animations - The animations to wait on.
	 * @returns {Promise<void>}
	 */
	function whenAllFinished(animations) {
		const endings = animations.map((animation) => {
			return animation.finished.catch(() => { });
		});
		return Promise.all(endings);
	}

	/**
	 * Resolves once every image has decoded, or once a timeout passes, whichever
	 * comes first, so a slow or broken image cannot stall the hero intro.
	 * @param {HTMLImageElement[]} images - The images to wait for.
	 * @returns {Promise<void>}
	 */
	function whenImagesReady(images) {
		const decoded = Promise.all(images.map(decodeImage));
		return Promise.race([decoded, wait(LOAD.heroTimeout)]);
	}

	/**
	 * Resolves when one image has decoded and is ready to paint without a hitch.
	 * Resolves anyway on any error, since a failed decode should not block the
	 * intro, and resolves at once where the browser lacks Image.decode.
	 * @param {HTMLImageElement} image - The image to decode.
	 * @returns {Promise<void>}
	 */
	function decodeImage(image) {
		if (typeof image.decode !== 'function') {
			return Promise.resolve();
		}
		return image.decode().catch(() => { });
	}

	/**
	 * A promise that resolves after a delay.
	 * @param {number} ms - Milliseconds to wait.
	 * @returns {Promise<void>}
	 */
	function wait(ms) {
		return new Promise((resolve) => {
			setTimeout(resolve, ms);
		});
	}

	/**
	 * Calls onEnter once for each element the first time it scrolls into view, then
	 * stops watching that element.
	 * @param {Element[]} elements - The elements to watch.
	 * @param {(element: Element) => void} onEnter - Run when an element first intersects.
	 * @param {{ rootMargin: string, threshold: number }} options - Observer tuning.
	 * @returns {void}
	 */
	function observeOnce(elements, onEnter, options) {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) {
					return;
				}
				onEnter(entry.target);
				observer.unobserve(entry.target);
			});
		}, options);

		elements.forEach((element) => {
			observer.observe(element);
		});
	}

	/**
	 * The position of an element among its parent's children, used to stagger
	 * groups like the pillars and the gallery.
	 * @param {Element} element - The element to locate.
	 * @returns {number} - Its zero-based index.
	 */
	function indexInParent(element) {
		return Array.prototype.indexOf.call(element.parentElement.children, element);
	}

	/**
	 * Whether to run animations at all: the browser needs the APIs we rely on, and
	 * the visitor must not have asked for reduced motion.
	 * @returns {boolean}
	 */
	function shouldAnimate() {
		const hasObserver = typeof IntersectionObserver === 'function';
		const hasWebAnimations = typeof Element.prototype.animate === 'function';
		if (!hasObserver || !hasWebAnimations) {
			return false;
		}
		if (prefersReducedMotion()) {
			return false;
		}
		return true;
	}

	/**
	 * Whether the visitor has asked the operating system to minimise motion.
	 * @returns {boolean}
	 */
	function prefersReducedMotion() {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}
})();
